"use client";

import { useEffect, useSyncExternalStore } from "react";
import { usePref } from "@/shared/prefs/use-pref";

export const THEMES = ["light", "dark", "system"] as const;
export type ThemePreference = (typeof THEMES)[number];
export const THEME_KEY = "mozaic-theme";

/** Runs before first paint (inlined in the root layout) so the page never flashes the wrong theme. */
export const themeInitScript = `try{var t=localStorage.getItem("${THEME_KEY}");var d=t==="dark"||((t===null||t==="system")&&matchMedia("(prefers-color-scheme: dark)").matches);document.documentElement.classList.toggle("dark",d)}catch(e){}`;

export function useThemePreference() {
  return usePref<ThemePreference>(THEME_KEY, "system", THEMES);
}

/** Applies the preference to <html> and follows OS changes while the preference is "system". */
export function ThemeSync() {
  const [preference] = useThemePreference();
  useEffect(() => {
    const query = window.matchMedia("(prefers-color-scheme: dark)");
    const apply = () => {
      const dark = preference === "dark" || (preference === "system" && query.matches);
      document.documentElement.classList.toggle("dark", dark);
    };
    apply();
    query.addEventListener("change", apply);
    return () => query.removeEventListener("change", apply);
  }, [preference]);
  return null;
}

/** True when <html> currently carries the dark class; re-renders on change. */
export function useIsDark() {
  return useSyncExternalStore(
    (listener) => {
      const observer = new MutationObserver(listener);
      observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
      return () => observer.disconnect();
    },
    () => document.documentElement.classList.contains("dark"),
    () => false,
  );
}
